/**
 * Calculate the damp rate.
 *
 * @param {number} damping - The damping.
 * @param {number} dt - The delta time.
 * @returns {number} - The lerp rate.
 */
export const damp = (damping, dt) => 1 - Math.pow(damping, dt * 1000);

// -------------------------------------------------
// 1.  Build / keep the cache entry
// --------------------------------------------------
export function buildPathCache(pathD) {
    const path2d   = new Path2D(pathD);

    const svgPath  = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    svgPath.setAttribute('d', pathD);

    return { path2d, svgPath };
}


// -------------------------------------------------
// 2.  Fast helpers that take the cache entry
// --------------------------------------------------
const _scratchCanvas = document.createElement('canvas');
_scratchCanvas.width = _scratchCanvas.height = 1;
const _ctx = _scratchCanvas.getContext('2d');

/* inside / outside --------------------------------*/
export function isPointInCachedPath(cache, x, y) {
    return _ctx.isPointInPath(cache.path2d, x, y);
}

/**
 * Return the closest point on an SVG path (already cached) to (x,y).
 *
 * cache     : {path2d, svgPath}  from buildPathCache(...)
 * x, y      : query point
 * coarse    : initial coarse step in user units  (default 4)
 * epsilon   : target precision in user units     (default 0.25)
 * @param cache
 * @param x
 * @param y
 * @param coarse
 * @param epsilon
 */
export function closestPointInCachedPath(
    cache, x, y, coarse = 32, epsilon = 1e-4
) {
    const p   = cache.svgPath;
    const len = p.getTotalLength();

    /* 1 Coarse scan ------------------------------------------------------- */
    let bestL = 0, bestPt = null, bestD2 = Infinity;
    for (let l = 0; l <= len; l += len / coarse) {
        const pt = p.getPointAtLength(l);
        const dx = pt.x - x, dy = pt.y - y, d2 = dx * dx + dy * dy;
        if (d2 < bestD2) {
            bestD2 = d2; bestL = l; bestPt = pt;
        }
    }

    /* 2 Refine until delta < epsilon ------------------------------------- */
    let delta = coarse / 2;
    while (delta > epsilon) {
        let improved = false;

        for (const dir of [-1, 1]) {
            const l  = Math.max(0, Math.min(len, bestL + dir * delta));
            const pt = p.getPointAtLength(l);
            const dx = pt.x - x, dy = pt.y - y, d2 = dx * dx + dy * dy;

            if (d2 < bestD2) {
                bestD2   = d2;
                bestL    = l;
                bestPt   = pt;
                improved = true;
            }
        }
        if (!improved) delta /= 2;   // no better point → shrink the window
    }

    return { x: bestPt.x, y: bestPt.y, distance: Math.sqrt(bestD2) };
}
